import React, { useEffect, useState } from 'react'
import * as CronofyElements from 'cronofy-elements'
import { CronofyCallbackNotificationSlot, CronofyElementMethods } from 'cronofy-elements'
import moment from 'moment-timezone'
import { useAuthContext } from '../../../context/auth'
import { useSchedulerContext } from '../../../context/scheduler/scheduler'
import './index.less'

export type TSchedulerAvailabilityViewerProps = {
	cronofy_account_id: string
	duration: number
	onAction: (slot: CronofyCallbackNotificationSlot) => void
}

function SchedulerAvailabilityViewer({ cronofy_account_id, duration, onAction }: TSchedulerAvailabilityViewerProps) {
	const { auth } = useAuthContext()
	const { scheduler, actions } = useSchedulerContext()

	const [cronofyElementMethods, setCronofyElementMethods] = useState<CronofyElementMethods | null>(null)
	useEffect(() => {
		if (cronofy_account_id && !scheduler?.availabilityViewerElementToken) {
			actions.getAvailabilityViewerElementToken(cronofy_account_id)
		}
	}, [scheduler?.availabilityViewerElementToken])

	useEffect(() => {
		if (!scheduler?.availabilityViewerElementToken) {
			return
		}

		// don't allow same day bookings - only 1+ days out
		const queryPeriodMonths = 3
		const queryPeriods = new Array(queryPeriodMonths).fill(null).map((_, month) => ({
			start: moment()
				.tz(auth?.user?.timezone)
				.add(1, 'day')
				.add(month, 'month')
				.startOf('day')
				.format('YYYY-MM-DDTHH:mm:ss[Z]'),
			end: moment()
				.tz(auth?.user?.timezone)
				.add(month + 1, 'month')
				.endOf('day')
				.format('YYYY-MM-DDTHH:mm:ss[Z]'),
		}))

		if (cronofy_account_id && scheduler?.availabilityViewerElementToken && !cronofyElementMethods) {
			setCronofyElementMethods(
				CronofyElements.DateTimePicker({
					element_token: scheduler.availabilityViewerElementToken,
					target_id: 'scheduler-viewer',
					locale: navigator.language || 'en',
					availability_query: {
						participants: [
							{
								required: 1,
								members: [
									{
										sub: cronofy_account_id,
										managed_availability: true,
									},
								],
							},
						],
						query_periods: queryPeriods,
						required_duration: { minutes: duration },
						response_format: 'overlapping_slots',
						start_interval: { minutes: 30 },
					},
					styles: {
						prefix: 'availability-datetime_picker',
					},
					max_results: 512,
					callback: ({ notification }) => {
						if (notification.type === 'slot_selected' && !!notification.slot) {
							onAction(notification.slot)
						}
					},
					config: {
						interval: 30,
						mode: 'no_confirm',
					},
					tzid: auth?.user?.timezone,
				})
			)
		} else if (cronofy_account_id && scheduler?.availabilityViewerElementToken && cronofyElementMethods) {
			cronofyElementMethods.update({
				element_token: scheduler.availabilityViewerElementToken,
				target_id: 'scheduler-viewer',
				locale: navigator.language || 'en',
				availability_query: {
					participants: [
						{
							required: 1,
							members: [
								{
									sub: cronofy_account_id,
									managed_availability: true,
								},
							],
						},
					],
					query_periods: queryPeriods,
					required_duration: { minutes: duration },
					response_format: 'overlapping_slots',
					start_interval: { minutes: 30 },
				},
				max_results: 512,
				callback: ({ notification }) => {
					if (notification.type === 'slot_selected' && !!notification.slot) {
						onAction(notification.slot)
					}
				},
				config: {
					interval: 30,
					mode: 'no_confirm',
				},
				tzid: auth?.user?.timezone,
			})
		}
	}, [auth, cronofy_account_id, scheduler?.availabilityViewerElementToken, duration])

	useEffect(() => {
		return () => {
			actions.clearAvailabilityViewerElementToken()
		}
	}, [])

	return <div id="scheduler-viewer" />
}

SchedulerAvailabilityViewer.defaultProps = {
	duration: 50,
}

export default SchedulerAvailabilityViewer
